import type React from "react";
import { useClientTranslations } from "../../lib/i18n/client";
import styles from "./ProfileModal.module.css";

interface ProfileModalProps {
	isOpen: boolean;
	onClose: () => void;
	title: string;
	children: React.ReactNode;
	onSave: () => void;
	saveButtonText?: string;
	hideSaveButton?: boolean;
}

export function ProfileModal({
	isOpen,
	onClose,
	title,
	children,
	onSave,
	saveButtonText = "Save",
	hideSaveButton = false,
}: ProfileModalProps) {
	const { t, direction } = useClientTranslations("profile");

	if (!isOpen) return null;

	return (
		<div className={styles.overlay}>
			<div className={styles.modal} dir={direction}>
				{/* Modal Header */}
				<div className={styles.header}>
					<h2 className={styles.title}>{title}</h2>
					<button
						type="button"
						onClick={onClose}
						className={styles.closeButton}
						aria-label="Close modal"
					>
						<svg
							className={styles.closeIcon}
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
						>
							<title>Close</title>
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								strokeWidth={2}
								d="M6 18L18 6M6 6l12 12"
							/>
						</svg>
					</button>
				</div>

				{/* Modal Content - Scrollable */}
				<div className={styles.content}>{children}</div>

				{/* Modal Actions - Fixed at bottom */}
				<div className={styles.actions}>
					<div className={styles.buttonGroup}>
						<button
							type="button"
							onClick={onClose}
							className={`${styles.button} ${styles.cancelButton} ${hideSaveButton ? styles.fullWidth : ""}`}
						>
							{t("common.cancel")}
						</button>
						{!hideSaveButton && (
							<button
								type="button"
								onClick={onSave}
								className={`${styles.button} ${styles.saveButton}`}
							>
								{saveButtonText}
							</button>
						)}
					</div>
				</div>
			</div>
		</div>
	);
}
